import { BaseModel, type RelationConfig } from './base';

import { Role } from './role';
import { EmployeeService } from './employeeService';
import { CustomField } from './customField';

import type { Service } from './service';

import type { Images } from '../types/interfaces/images';
import type { Permissions } from '../types/interfaces/permissions';
export class Employee extends BaseModel {
  id!: number
  idEmployee!: number
  type!: EmployeeType
  firstName!: string
  lastName!: string
  displayName!: string
  bio!: string
  enabled!: boolean
  deleted!: number
  hasImage!: boolean
  listOrder!: number
  email!: string | null
  phone!: string
  resourceTypes!: any[]
  allowInternet!: boolean
  overrides!: {
    hasPriceOverrides: boolean
    hasTimingOverrides: boolean
    hasZeroPricesOverrides: boolean
  }
  minimiseGapsSettings!: {
    enabled: boolean
    allowStartOfDay: boolean
    allowEndOfDay: boolean
    allowAlongsideBlocks: boolean
    allowWithMinimumGap: number
    allowAnyOnEmptyDay: boolean
    overridden: boolean
  }
  resourceRostered!: boolean
  hasPIN!: boolean

  images?: Images
  permissions?: Permissions

  role?: Role
  services?: EmployeeService[]
  customFields?: CustomField[]

  // Used for employee-service relationship lookups
  employeeServiceData?: EmployeeService[]

  static override relations(): RelationConfig {
    return {
      role: { type: Role },
      services: { type: EmployeeService },
      customFields: { type: CustomField }
    };
  }

  override objectID(): string {
    return this.idEmployee?.toString() || '';
  }

  get label(): string {
    return this.displayName;
  }

  get image(): string | null {
    if (this.hasImage) {
      return this.images?.square || null;
    }
    return null;
  }

  get initials(): string {
    return (
      (this.firstName ? this.firstName.charAt(0) : '') +
      (this.lastName ? this.lastName.charAt(0) : '')
    ).toUpperCase();
  }

  get overridesLabels(): string[] {
    const labels: string[] = [];
    if (this.overrides) {
      if (this.overrides.hasPriceOverrides && !this.overrides.hasZeroPricesOverrides) {
        labels.push('employee.hasPriceOverrides');
      }
      if (this.overrides.hasZeroPricesOverrides) {
        labels.push('employee.hasZeroPricesOverrides');
      }
      if (this.overrides.hasTimingOverrides) {
        labels.push('employee.hasTimingOverrides');
      }
    }
    return labels;
  }

  get hasOverrides(): boolean {
    return !!(
      this.overrides?.hasPriceOverrides ||
      this.overrides?.hasTimingOverrides ||
      this.overrides?.hasZeroPricesOverrides
    );
  }

  get isDeleted(): boolean {
    return !!this.deleted;
  }

  get isEmployeeType(): boolean {
    return this.type === EmployeeType.Employee;
  }

  get isResourceType(): boolean {
    return this.type === EmployeeType.Resource;
  }

  get isAdministratorType(): boolean {
    return this.type === EmployeeType.Admin;
  }

  static compare(lhs: Employee | null, rhs: Employee | null): number | null {
    if (lhs && !rhs) return -1;
    if (rhs && !lhs) return 1;
    if (!rhs && !lhs) return null;

    if (lhs?.type === rhs?.type) {
      return (lhs?.listOrder || 0) - (rhs?.listOrder || 0);
    }

    return (lhs?.type || 0) - (rhs?.type || 0);
  }

  getEmployeeServiceForService(service: Service): EmployeeService | null {
    if (this.employeeServiceData) {
      for (const employeeService of this.employeeServiceData) {
        if (employeeService.service?.idService === service.idService) {
          return employeeService;
        }
      }
    }

    if (service.employees) {
      for (const employeeService of service.employees) {
        if (employeeService.employee?.idEmployee === this.idEmployee) {
          return employeeService;
        }
      }
    }

    return null;
  }
}

export enum EmployeeType {
  Employee = 0,
  Resource = 1,
  Admin = 2
}

export const EmployeeTypeLabels: Record<EmployeeType, string> = {
  [EmployeeType.Employee]: 'employee.employee',
  [EmployeeType.Resource]: 'employee.resource',
  [EmployeeType.Admin]: 'employee.admin',
};
